import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";
import {
  Calendar,
  Search,
  Plus,
  Pencil,
  Trash2,
  ArrowLeft,
  X,
  Clock,
  User,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import SessionForm from "@/components/sessions/SessionForm";
import { format } from "date-fns";

const STATUS_PILLS = [
  { id: "all", label: "All" },
  { id: "upcoming", label: "Upcoming" },
  { id: "pending", label: "Pending Review" },
  { id: "completed", label: "Completed" },
  { id: "cancelled", label: "Cancelled" },
];

const STATUS_COLORS = {
  upcoming: "bg-sky-500/15 text-sky-400 border-sky-500/30",
  pending: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  completed: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  cancelled: "bg-red-500/15 text-red-400 border-red-500/30",
};

export default function AdminSessionsSection({
  onBack,
  initialFilter = "all",
}) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState(initialFilter);
  const [showForm, setShowForm] = useState(false);
  const [editingSession, setEditingSession] = useState(null);

  const { data: sessions = [], isLoading } = useQuery({
    queryKey: ["admin_training_sessions"],
    queryFn: () => base44.entities.TrainingSession.list("-date", 250),
  });

  const { data: trainers = [] } = useQuery({
    queryKey: ["admin_trainers_lookup"],
    queryFn: () => base44.entities.Trainer.list(),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.TrainingSession.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin_training_sessions"] });
      setShowForm(false);
      setEditingSession(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => base44.entities.TrainingSession.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin_training_sessions"] });
    },
  });

  const getTrainerName = (trainerId) => {
    const t = trainers.find((tr) => tr.id === trainerId || tr.user_id === trainerId);
    return t ? t.name : "Trainer";
  };

  const filteredSessions = useMemo(() => {
    return sessions.filter((s) => {
      const q = search.toLowerCase().trim();
      const matchSearch =
        !q ||
        (s.title && s.title.toLowerCase().includes(q)) ||
        (s.client_name && s.client_name.toLowerCase().includes(q)) ||
        (s.notes && s.notes.toLowerCase().includes(q));

      if (!matchSearch) return false;

      const st = (s.status || "upcoming").toLowerCase();
      if (filter === "all") return true;
      if (filter === "pending") return st === "pending";
      if (filter === "upcoming") return st === "upcoming";
      if (filter === "completed") return st === "completed";
      if (filter === "cancelled") return st === "cancelled";
      return true;
    });
  }, [sessions, search, filter]);

  return (
    <div className="space-y-4 pb-20">
      {/* Top Bar with Back navigation & Title */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onBack}
            className="p-1.5 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-slate-300 hover:text-white transition-all cursor-pointer"
            aria-label="Back to Command Center"
          >
            <ArrowLeft className="w-4 h-4 text-sky-400" />
          </button>
          <div>
            <h1 className="text-lg font-bold text-white leading-tight">Appointments</h1>
            <p className="text-[11px] text-slate-400">
              {filteredSessions.length} of {sessions.length} sessions
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={() => {
            setEditingSession(null);
            setShowForm(true);
          }}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-sky-500 hover:bg-sky-400 text-slate-950 font-bold text-xs shadow-md active:scale-95 transition-all cursor-pointer"
          data-testid="book-session-btn"
        >
          <Plus className="w-3.5 h-3.5" />
          <span>Book Session</span>
        </button>
      </div>

      {/* Search Input */}
      <div className="relative">
        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by client or session notes…"
          className="w-full pl-9 pr-8 py-2 rounded-xl bg-[#071326] border border-white/10 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-sky-400"
        />
        {search && (
          <button
            type="button"
            onClick={() => setSearch("")}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Status Filter Horizontal Pills */}
      <div className="flex items-center gap-1.5 overflow-x-auto pb-1 no-scrollbar">
        {STATUS_PILLS.map((pill) => {
          const isActive = filter === pill.id;
          return (
            <button
              key={pill.id}
              type="button"
              onClick={() => setFilter(pill.id)}
              className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap transition-all cursor-pointer ${
                isActive
                  ? "bg-sky-500 text-slate-950 shadow-sm"
                  : "bg-white/5 text-slate-400 hover:text-white border border-white/5"
              }`}
            >
              {pill.label}
            </button>
          );
        })}
      </div>

      {/* Session Cards List */}
      {isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-24 rounded-2xl bg-white/5 animate-pulse" />
          ))}
        </div>
      ) : filteredSessions.length === 0 ? (
        <div className="rounded-2xl p-8 bg-[#071326] border border-white/10 text-center space-y-2">
          <Calendar className="w-8 h-8 text-slate-500 mx-auto" />
          <p className="text-xs font-bold text-white">No appointments found</p>
          <p className="text-[11px] text-slate-400">
            {search ? "No sessions match your search." : "No records match the active filter."}
          </p>
        </div>
      ) : (
        <div className="space-y-2.5">
          {filteredSessions.map((session) => {
            const st = (session.status || "upcoming").toLowerCase();
            const statusStyle = STATUS_COLORS[st] || STATUS_COLORS.upcoming;
            let formattedDate = "";
            try {
              if (session.date) {
                formattedDate = format(new Date(session.date), "EEE, MMM d • h:mm a");
              }
            } catch (_) {
              formattedDate = String(session.date || "");
            }

            return (
              <div
                key={session.id}
                className="p-3.5 rounded-2xl bg-gradient-to-r from-[#08152B] to-[#040C1A] border border-white/10 hover:border-sky-400/40 transition-all shadow-sm"
                data-testid={`session-card-${session.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="text-xs font-bold text-white truncate">
                        {session.title || "Training Session"}
                      </span>
                      <span
                        className={`px-2 py-0.5 rounded-full text-[9px] font-bold border capitalize ${statusStyle}`}
                      >
                        {st}
                      </span>
                    </div>

                    <div className="flex items-center gap-2 text-[11px] text-sky-400 font-semibold mb-1">
                      <Clock className="w-3.5 h-3.5 shrink-0" />
                      <span>{formattedDate}</span>
                      {session.duration_minutes && (
                        <span className="text-slate-400">({session.duration_minutes} min)</span>
                      )}
                    </div>

                    <div className="flex items-center gap-3 text-[10px] text-slate-400">
                      <span className="flex items-center gap-1">
                        <User className="w-3 h-3 text-slate-400" />
                        {session.client_name || session.client_email || "Client"}
                      </span>
                      {session.trainer && (
                        <span>Coach: {getTrainerName(session.trainer)}</span>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1 shrink-0">
                    {st === "upcoming" && (
                      <button
                        type="button"
                        onClick={() =>
                          updateMutation.mutate({ id: session.id, data: { status: "completed" } })
                        }
                        className="p-2 rounded-xl bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 active:scale-95 transition-all cursor-pointer"
                        title="Mark Completed"
                      >
                        <CheckCircle2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                    {st !== "cancelled" && (
                      <button
                        type="button"
                        onClick={() =>
                          updateMutation.mutate({ id: session.id, data: { status: "cancelled" } })
                        }
                        className="p-2 rounded-xl bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/30 active:scale-95 transition-all cursor-pointer"
                        title="Cancel Session"
                      >
                        <XCircle className="w-3.5 h-3.5" />
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        setEditingSession(session);
                        setShowForm(true);
                      }}
                      className="p-2 rounded-xl bg-white/5 hover:bg-sky-500/20 text-slate-300 hover:text-sky-300 border border-white/10 active:scale-95 transition-all cursor-pointer"
                      title="Edit Session"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Session Form */}
      {showForm && (
        <SessionForm
          open={showForm}
          onOpenChange={(isOpen) => {
            if (!isOpen) {
              setShowForm(false);
              setEditingSession(null);
            }
          }}
          session={editingSession}
          onSubmit={async (data) => {
            if (editingSession) {
              await updateMutation.mutateAsync({ id: editingSession.id, data });
            } else {
              await base44.entities.TrainingSession.create(data);
              queryClient.invalidateQueries({ queryKey: ["admin_training_sessions"] });
            }
            setShowForm(false);
            setEditingSession(null);
          }}
        />
      )}
    </div>
  );
}
